import { useForm, useWatch, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Trash2 } from 'lucide-react'
import { slideInRight } from '@/lib/motion'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { TimePicker } from '@/components/ui/TimePicker'
import { DatePicker } from '@/components/ui/DatePicker'
import { useBodyScrollLock } from '@/components/ui/Modal'
import { useCreateOpeningHour, useUpdateOpeningHour, useDeleteOpeningHour } from '@/lib/queries/useOpeningHours'
import {
  normalizeOpeningHourMode,
  type OpeningHour,
  type OpeningHourSchedule,
} from '@/lib/types/openingHour'

// HHMM integer (e.g. 900, 2200) ↔ "HH:MM" string
function hhmmToTime(val: number): string {
  const h = Math.floor(val / 100)
  const m = val % 100
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}
function timeToHhmm(val: string): number {
  const [h, m] = val.split(':').map(Number)
  return h * 100 + (m || 0)
}

const DAYS = [
  { value: '1', label: 'Monday' },
  { value: '2', label: 'Tuesday' },
  { value: '3', label: 'Wednesday' },
  { value: '4', label: 'Thursday' },
  { value: '5', label: 'Friday' },
  { value: '6', label: 'Saturday' },
  { value: '7', label: 'Sunday' },
]

const schema = z.object({
  mode: z.enum(['booking', 'order', 'delivery', 'restaurant', 'takeaway']),
  day: z.coerce.number().min(1).max(7),
  openTime: z.string().regex(/^\d{2}:\d{2}$/),
  closeTime: z.string().regex(/^\d{2}:\d{2}$/),
  maxBookings: z.coerce.number().min(0),
  maxGuests: z.coerce.number().min(0),
  seatingActive: z.boolean(),
  seatingInterval: z.coerce.number().min(1).optional(),
  seatingDuration: z.coerce.number().min(1).optional(),
  useWaitlist: z.boolean(),
  paymentActive: z.boolean(),
  note: z.string().optional(),
  special: z.boolean(),
  endDate: z.string().optional(),
})

type FormData = z.infer<typeof schema>

function getDefaults(
  hour?: OpeningHour | null,
  defaultDay?: number,
  defaultSpecial?: boolean,
  defaultMode?: OpeningHourSchedule,
): FormData {
  return {
    mode: hour ? normalizeOpeningHourMode(hour.mode) : (defaultMode ?? 'booking'),
    day: hour?.day ?? defaultDay ?? 1,
    openTime: hour ? hhmmToTime(hour.open) : '11:00',
    closeTime: hour ? hhmmToTime(hour.close) : '22:00',
    maxBookings: hour?.maxBookings ?? 0,
    maxGuests: hour?.maxGuests ?? 0,
    seatingActive: hour?.seatingActive ?? false,
    seatingInterval: hour?.seatingInterval ?? 30,
    seatingDuration: hour?.seatingDuration ?? 90,
    useWaitlist: hour?.useWaitlist ?? false,
    paymentActive: hour?.paymentActive ?? false,
    note: hour?.note ?? '',
    special: hour?.special ?? defaultSpecial ?? false,
    endDate: hour?.endDate ?? '',
  }
}

interface OpeningHourFormProps {
  open: boolean
  onClose: () => void
  hour?: OpeningHour | null
  defaultDay?: number
  defaultSpecial?: boolean
  defaultMode?: OpeningHourSchedule
}

export function OpeningHourForm({
  open,
  onClose,
  hour,
  defaultDay,
  defaultSpecial,
  defaultMode,
}: OpeningHourFormProps) {
  useBodyScrollLock(open)
  const isEdit = !!hour
  const create = useCreateOpeningHour()
  const update = useUpdateOpeningHour()
  const deleteHour = useDeleteOpeningHour()

  const { register, handleSubmit, control, reset, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema) as any,
    defaultValues: getDefaults(hour, defaultDay, defaultSpecial, defaultMode),
  })

  // Reset whenever the panel opens or the target hour changes
  useEffect(() => {
    if (open) reset(getDefaults(hour, defaultDay, defaultSpecial, defaultMode))
  }, [open, hour, defaultDay, defaultSpecial, defaultMode, reset])

  const seatingActive = useWatch({ control, name: 'seatingActive' })
  const isSpecial = useWatch({ control, name: 'special' })

  const onSubmit = (data: FormData) => {
    const payload = {
      mode: data.mode,
      day: data.day,
      open: timeToHhmm(data.openTime),
      close: timeToHhmm(data.closeTime),
      maxBookings: data.maxBookings,
      maxGuests: data.maxGuests,
      seatingActive: data.seatingActive,
      seatingInterval: data.seatingActive ? data.seatingInterval : null,
      seatingDuration: data.seatingActive ? data.seatingDuration : null,
      useWaitlist: data.useWaitlist,
      paymentActive: data.paymentActive,
      note: data.note,
      special: data.special,
      endDate: data.special ? (data.endDate || null) : null,
    }
    if (isEdit && hour) {
      update.mutate({ documentId: hour.documentId, ...payload } as any, { onSuccess: onClose })
    } else {
      create.mutate({ ...payload, bookosId: `oh-${Date.now()}` } as any, { onSuccess: onClose })
    }
  }

  const handleDelete = () => {
    if (hour) deleteHour.mutate(hour.documentId, { onSuccess: onClose })
  }

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[900] bg-black/60"
            onClick={onClose}
          />
          <motion.div
            {...slideInRight}
            className="fixed inset-0 z-[901] flex flex-col bg-[var(--color-bg-surface)] shadow-2xl sm:inset-y-0 sm:left-auto sm:right-0 sm:w-full sm:max-w-md sm:border-l sm:border-[var(--color-border-subtle)]"
          >
            {/* Header */}
            <div className="flex shrink-0 items-center justify-between gap-3 border-b border-[var(--color-border-subtle)] px-4 pb-4 pt-[calc(1rem+env(safe-area-inset-top))]">
              <h3 className="font-display font-semibold text-[var(--color-text-primary)]">
                {isEdit ? 'Edit Opening Hours' : 'Add Opening Hours'}
              </h3>
              <button
                onClick={onClose}
                aria-label="Close opening hours form"
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[var(--radius-md)] text-[var(--color-text-muted)] transition-colors hover:bg-[var(--color-bg-elevated)] hover:text-[var(--color-text-primary)]"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="min-h-0 flex-1 touch-pan-y space-y-4 overflow-y-auto overscroll-contain p-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">

              {/* Special date toggle */}
              <label className="flex items-center gap-2 text-sm text-[var(--color-text-secondary)] cursor-pointer">
                <input type="checkbox" className="accent-[var(--color-accent-amber)]" {...register('special')} />
                Special date override (one-off)
              </label>

              {/* Day selector — hidden for special dates (date replaces day) */}
              {!isSpecial && (
                <Select
                  label="Day"
                  options={DAYS}
                  {...register('day')}
                  error={(errors.day as any)?.message}
                />
              )}

              {/* Special date picker */}
              {isSpecial && (
                <Controller
                  control={control}
                  name="endDate"
                  render={({ field }) => (
                    <DatePicker
                      value={field.value ?? ''}
                      onChange={field.onChange}
                      label="Date"
                      placeholder="Pick the special date"
                    />
                  )}
                />
              )}

              <Select
                label="Schedule"
                options={[
                  { value: 'booking', label: 'Bookings' },
                  { value: 'order', label: 'Orders' },
                  { value: 'delivery', label: 'Delivery' },
                ]}
                {...register('mode')}
              />

              {/* Open / Close time */}
              <div className="grid grid-cols-2 gap-3">
                <Controller
                  control={control}
                  name="openTime"
                  render={({ field }) => (
                    <TimePicker
                      value={field.value}
                      onChange={field.onChange}
                      label="Opens at"
                      error={(errors as any).openTime?.message}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="closeTime"
                  render={({ field }) => (
                    <TimePicker
                      value={field.value}
                      onChange={field.onChange}
                      label="Closes at"
                      error={(errors as any).closeTime?.message}
                    />
                  )}
                />
              </div>

              {/* Capacity */}
              <div className="grid grid-cols-2 gap-3">
                <Input label="Max bookings (0 = unlimited)" type="number" {...register('maxBookings')} />
                <Input label="Max guests (0 = unlimited)" type="number" {...register('maxGuests')} />
              </div>

              {/* Toggles */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm text-[var(--color-text-secondary)] cursor-pointer">
                  <input type="checkbox" className="accent-[var(--color-accent-amber)]" {...register('seatingActive')} />
                  Seating slots active
                </label>
                <label className="flex items-center gap-2 text-sm text-[var(--color-text-secondary)] cursor-pointer">
                  <input type="checkbox" className="accent-[var(--color-accent-amber)]" {...register('useWaitlist')} />
                  Use waitlist
                </label>
                <label className="flex items-center gap-2 text-sm text-[var(--color-text-secondary)] cursor-pointer">
                  <input type="checkbox" className="accent-[var(--color-accent-amber)]" {...register('paymentActive')} />
                  Payment required
                </label>
              </div>

              {/* Seating interval + duration — visible only when seating is active */}
              {seatingActive && (
                <div className="grid grid-cols-2 gap-3 p-3 rounded-[var(--radius-md)] bg-[var(--color-bg-elevated)] border border-[var(--color-border-subtle)]">
                  <Input
                    label="Slot interval (min)"
                    type="number"
                    {...register('seatingInterval')}
                    error={errors.seatingInterval?.message}
                  />
                  <Input
                    label="Slot duration (min)"
                    type="number"
                    {...register('seatingDuration')}
                    error={errors.seatingDuration?.message}
                  />
                </div>
              )}

              <Input label="Internal note" {...register('note')} />

              <div className="flex gap-2 pt-2">
                {isEdit && (
                  <Button
                    type="button"
                    variant="danger"
                    size="md"
                    onClick={handleDelete}
                    loading={deleteHour.isPending}
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete
                  </Button>
                )}
                <Button
                  type="submit"
                  className="flex-1"
                  loading={create.isPending || update.isPending}
                >
                  {isEdit ? 'Save Changes' : 'Add Hours'}
                </Button>
              </div>
            </form>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
